<template>
    <div id="main-wrapper" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">

        <AdminNavbar />

        <div class="page-title">
            <div class="container">
                <div class="row">
                    <div class="col-lg-12 col-md-12">
                        <h2 class="ipt-title">{{ $t('Welcome!') }}</h2>
                        <span class="ipn-subtitle">
                            {{ $t('Welcome To Your Account') }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <section class="bg-light">
            <div class="container-fluid">

                <div class="row">
                    <div class="col-lg-12 col-md-12">
                        <div class="filter_search_opt">
                            <router-link
                                to=""
                                @click.prevent="show = !show"
                                class="btn btn-dark full-width mb-4"
                            >
                                {{ $t('Dashboard Navigation') }}
                                <i class="fa-solid fa-bars ms-2 px-2"></i>
                            </router-link>
                        </div>
                    </div>
                </div>

                <div class="row">

                    <div class="col-lg-3 col-md-12">
                        <AdminSidebar
                            :open="show"
                            @update:open="show = $event"
                            :userData="userData"
                            :category-name="categoryName"
                            :state-name="stateName"
                        />
                    </div>

                    <div class="col-lg-9 col-md-12">

                        <div class="dashboard-wraper">
                            <div class="d-flex justify-content-between align-items-center mb-4">
                                <h4 class="mb-0">
                                    {{ $t('Branches') }}
                                </h4>

                                <button
                                    type="button"
                                    class="btn btn-primary"
                                    @click="addBranch"
                                >
                                    <i class="fa-solid fa-plus me-2"></i>
                                    {{ $t('Add Branch') }}
                                </button>
                            </div>

                            <div v-if="loading" class="text-center py-5">
                                <div class="spinner-border" role="status">
                                    <span class="visually-hidden">
                                        Loading...
                                    </span>
                                </div>
                            </div>

                            <div
                                v-else-if="branches.length === 0"
                                class="alert alert-info"
                            >
                                {{ $t('No branches found.') }}
                            </div>

                            <div
                                v-else
                                class="table-responsive"
                            >
                                <table class="table table-bordered table-hover align-middle">

                                    <thead class="table-light">
                                        <tr>
                                            <th>#</th>
                                            <th>{{ $t('Name') }}</th>
                                            <th>{{ $t('City') }}</th>
                                            <th>{{ $t('Area') }}</th>
                                            <th>{{ $t('Address') }}</th>
                                            <th>{{ $t('Type') }}</th>
                                            <th>{{ $t('Actions') }}</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr
                                            v-for="(branch, index) in branches"
                                            :key="branch.id ?? `branch-${index}`"
                                        >
                                            <td>
                                                {{ index + 1 }}
                                            </td>

                                            <td>
                                                {{ branch.name || '-' }}
                                            </td>

                                            <td>
                                                {{ branch.state || '-' }}
                                            </td>

                                            <td>
                                                {{ branch.city || '-' }}
                                            </td>

                                            <td>
                                                {{ branch.address || '-' }}
                                            </td>

                                            <td>
                                                <span
                                                    v-if="branch.type === 'main'"
                                                    class="badge bg-success"
                                                >
                                                    {{ $t('Main') }}
                                                </span>

                                                <span
                                                    v-else
                                                    class="badge bg-secondary"
                                                >
                                                    {{ $t('Branch') }}
                                                </span>
                                            </td>

                                            <td>
                                                <div class="d-flex gap-2">
                                                    <button
                                                        type="button"
                                                        class="btn btn-sm btn-warning"
                                                        @click="editBranch(branch)"
                                                        :disabled="!branch.id"
                                                        :title="$t('Edit')"
                                                    >
                                                        <i class="fa-solid fa-pen"></i>
                                                    </button>

                                                    <button
                                                        type="button"
                                                        class="btn btn-sm btn-danger"
                                                        @click="deleteBranch(branch)"
                                                        :disabled="!branch.id || branch.type === 'main'"
                                                        :title="$t('Delete')"
                                                    >
                                                        <i class="fa-solid fa-trash"></i>
                                                    </button>

                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>

                                </table>
                            </div>

                        </div>

                    </div>
                </div>
            </div>
        </section>

        <FooterTop :bg="'theme-bg'" />

        <FooterDark />

        <ScrollToTop />

    </div>
</template>


<script setup>

import { ref, computed, onMounted, watch } from 'vue';
import axios from 'axios';

import AdminSidebar from '@/components/admin-sidebar.vue';
import AdminNavbar from '@/components/navbar/admin-navbar.vue';
import FooterDark from '@/components/footer/footer-dark.vue';
import FooterTop from '@/components/footer/footer-top.vue';
import ScrollToTop from '@/components/scroll-to-top.vue';
import Swal from 'sweetalert2';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

const { t,locale } = useI18n();
const router = useRouter();

// Sidebar
const show = ref(false);
const token = localStorage.getItem('auth_token');
const userData = ref(null);

// Branches
const branches = ref([]);
const loading = ref(false);

// User data
const fetchUserData = async () => {
    try {
        const response = await axios.get(
            '/users/service-provider/show-details',
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json',
                    locale: locale.value
                }
            }
        );
        userData.value = response.data;
    } catch (error) {
        console.error('Error fetching user data:', error);
    }

};

// Category
const categoryName = computed(() => {

    return (
        userData.value
            ?.service_provider_details
            ?.category
            ?.name || ''
    );

});

// State
const stateName = computed(() => {

    return (
        userData.value
            ?.service_provider_details
            ?.state
            ?.name || ''
    );

});

// Fetch branches
const fetchBranches = async () => {

    loading.value = true;

    try {

        const response = await axios.get(
            '/service-provider/branch/list',
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json',
                    locale: locale.value
                }
            }
        );

        branches.value = response.data.branches || [];
    } catch (error) {

        console.error('Error fetching branches:', error);

    } finally {

        loading.value = false;

    }

};

watch(
    () => locale.value,
    () => {
        fetchBranches();
        fetchUserData();
    }
);

// Add branch
const addBranch = () => {
    router.push('/add-branch');
};


// Edit branch
const editBranch = (branch) => {
    if (!branch.id) {
        return;
    }

    router.push(`/edit-branch/${branch.id}`);
};


// Delete branch
const deleteBranch = async (branch) => {
    if (!branch.id) {
        return;
    }
    if (branch.type === 'main') {
        return;
    }
    const result = await Swal.fire({
        title: t('Are you sure?'),
        text: t('Are you sure you want to delete this branch?'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: t('Yes, delete it!'),
        cancelButtonText: t('Cancel'),
        reverseButtons: true
    });

    if (!result.isConfirmed) {
        return;
    }
    try {
        await axios.delete(
            `/service-provider/branch/delete/${branch.id}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json'
                }
            }
        );
        branches.value = branches.value.filter(
            item => item.id !== branch.id
        );

        await Swal.fire({
            title: t('Deleted!'),
            text: t('The branch has been deleted successfully.'),
            icon: 'success',
            confirmButtonText: t('OK')
        });

    } catch (error) {
        console.error('Error deleting branch:', error);
        Swal.fire({
            title: 'Error!',
            text: error.response?.data?.msg || 'Failed to delete the branch.',
            icon: 'error',
            confirmButtonText: 'OK'
        });
    }
};

// Mounted
onMounted(() => {
    fetchUserData();
    fetchBranches();
});
</script>

<style scoped>
.d-flex.gap-2 {
    align-items: center;
}
.table th,
.table td {
    vertical-align: middle;
}
</style>
